import {ServiceBase} from "../../ServiceBase";
import {
    ExternalLinkViewMode,
    IExternalLinksService, IOpenOmgOptions,
    OpenContactUsOptions,
    OpenLinkParams,
    OpenRichPageOptions
} from "../external-links.service.interface";
import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {OnPlayerActionParams} from "../../sdk/sdk-events";
import {CHANNEL_ELEMENTS, CHANNEL_SOURCE} from "../../../../common/const/casinofied-analytics-consts";
import {AreaType} from "@sparkware/uc-sdk-core";
import {MarketingParams} from "../../../../common/interfaces/marketing-params.interface";
import Device from "../../../../sdk/src/utils/device/device";
import {SdkOpenExternalLinkParams} from "../../sdk/sdk.service.interface";
import {GNWP_SOURCES} from "../../../../sdk/src/components/external_links/external_links_consts";
import {PRIORITY_LEVEL} from "../../../../sdk/src/services/overlay_manager/overlay_manager_levels";
import {IOpenMyAccountOptions} from "../handlers/my-account/my-account.external-link-handler.interface";
import {IOpenRegistrationOptions} from "../handlers/registration/registration.external-link-handler.interface";
import {OpenCashierOptions} from "../../../../common/interfaces/cashier.service.interface";


export class ExternalLinksLegacyStrategy extends ServiceBase implements IExternalLinksService {
    constructor(services: IServiceFactoryExtended) {
        super(services);

        this.services.sdk.events.onPlayerAction(this._onPlayerActionHandler.bind(this));
    }


    private _onPlayerActionHandler(params: OnPlayerActionParams) {
        if(params.actionName === 'deposit') {
            this.services.cashier.openCashier({
                ...params.options,
                channel: {
                    source: CHANNEL_SOURCE.registration,
                    area: AreaType.StraightToCashier,
                    element: CHANNEL_ELEMENTS.depositButton
                }
            });
        }
    }

    openLink(params: OpenLinkParams, marketingParams?: MarketingParams, useCallbackURL: boolean = false): Promise<any> {
        let marketingData = this.services.marketing.getMarketingData(marketingParams);
        const isHybrid = Device.hybrid.isHybrid;


        const sdkOpenParams: SdkOpenExternalLinkParams = {
            view: params.view,
            source: params.source,
            sourceLink: params.sourceLink,
            addCloseOption: params.addCloseOption,
            priorityLevel: params.priorityLevel,
            marketing: marketingData,
            options: {
                ...params.options,
                showRegister: !this.services.cmsSegmentation.isCisOrCip && this.services.configuration.data.generalSettings.showRegisterButton
            }
        };

        if(params.source === 'cashier') {
            return this.services.cashier.openCashier({
                ...sdkOpenParams.options,
                channel: {
                    source: CHANNEL_SOURCE.casinoClient,
                    area: AreaType.NavigationMenu,
                    element: CHANNEL_ELEMENTS.depositButton
                }

            });
        }

        return new Promise((resolve, reject) => {
            if(params.resolveByCallback && !isHybrid) {
                sdkOpenParams.actionCallback = (result: any) => resolve(result);
            }

            this.services.sdk.openExternalLink(sdkOpenParams, useCallbackURL).then(()=> {
                // In case there is an actionCallback this promise will be resolved by a call from inside sdk.openExternalLink
                if(!sdkOpenParams.actionCallback) {
                    resolve();
                }
            }).catch((err)=> {
                reject(err);
            });
        });
    }

    openResponsibleGamblingLink(view?:number):void{
        this.services.sdk.openResponsibleGamingLink(view);
    }


    async openMyAccount(options?: IOpenMyAccountOptions, marketingParams?: MarketingParams): Promise<void> {
        await this.openLink({
            source: 'my_account',
            options: options,
            resolveByCallback: false
        }, marketingParams);
    }

    async openCashier(options?: OpenCashierOptions): Promise<void> {
        await this.services.sdk.openExternalLink({
            source: GNWP_SOURCES.CASHIER,
            options: options
        });
    }

    async openContactUs(options?: OpenContactUsOptions, marketingParams?: MarketingParams, resolveByCallback: boolean = false): Promise<void> {
        await this.openLink({
            source: 'contact_us',
            view: options?.view,
            resolveByCallback: resolveByCallback
        }, marketingParams);
    }

    async openOldChat(): Promise<void> {
        await this.openLink({
            source: 'live_chat',
            view: 'window'
        });
    }

    async openHelpPage(): Promise<void> {
        await this.openLink({
            source: 'help',
            view: 'window',
            resolveByCallback: false
        });
    }

    async openDownloadApp(): Promise<void> {
        await this.openLink({
            sourceLink: this.services.sdk.getDownloadAppLink(),
            view: 'redirect',
            resolveByCallback: false
        });
    }

    async openUploadDocument(): Promise<void> {
        await this.openLink({
            source: 'upload_document',
        });
    }

    async openMembership(options?: IOpenRegistrationOptions): Promise<void> {
        await this.openLink({
            source: 'membership'
        }, options?.marketingParams);
    }

    async openPendingPlayApplication(token: string): Promise<void> {
        await this.openLink({
            source: GNWP_SOURCES.PENDING_PLAY,
            options: {
                token: token
            }
        });
    }

    async openSafeToPlay(): Promise<void> {
        const url = this.services.sdk.getSkin().getSafeToPlayURL();
        if(url) {
            await this.openLink({
                sourceLink: url,
                view: 'window'
            });
        }
    }

    async openRichPage(options: OpenRichPageOptions): Promise<void> {
        if(!options.campaignId) {
            throw new Error('Rich campaignId parameter was not provided');
        }

        const url = this.services.configuration.servicesConfigs.Integration.rich?.richPageUrl;
        if(!url) {
            throw new Error('Rich page url was not configured');
        }

        await this.services.location.redirect(url.replace('{campaignid}', options.campaignId));
    }

    async openSport(): Promise<void> {
        await this.openLink({
            source: GNWP_SOURCES.SPORT
        });
    }

    async openInternalUrl(url: string): Promise<void> {
        if(!url) {
            throw new Error('openInternalUrl failed! No url provided');
        }

        await this.services.location.push(url);

    }
    async openExternalUrl(url: string): Promise<void> {
        if(!url) {
            throw new Error('openExternalUrl failed! No url provided');
        }

        let view: ExternalLinkViewMode = this.services.device.os.isMobileOS ? 'iframe' : 'window';

        await this.openLink({
            sourceLink: url,
            addCloseOption: true,
            view: view,
            priorityLevel: PRIORITY_LEVEL.INTERNAL_OVER_EXTERNAL_LINK
        });
    }

    openExternalBrand(params: OpenLinkParams, marketingParams?: MarketingParams, useCallbackURL: boolean = false): Promise<any> {
        if (params.sourceLink) {
            const externalBrandLink = this.services.stateOfResidenceIdentifier.prepareUrlForExternalBrand(params.sourceLink);
            return this.openLink({...params, sourceLink: externalBrandLink}, marketingParams, useCallbackURL);
        } else {
            return this.openLink(params, marketingParams, useCallbackURL);
        }
    }

    openBankId(url: string): void {
        if(Device.hybrid.inCasino) {
            this.services.hybrid.openExternalLink(url);
        } else {
            /*
                BankId url is an url with custom schema something like 'bankid:///?autostarttoken
                This will basically rely on the browser that will ask the operating system to handle bankid schema which in the end will open an application.
                On some browsers this works with window.location.assign and on others with window.open. This is the reason we do those two subsequent calls.
                Only one of them will be succesfull. And even if both will succeed the BankID application is a single instance application so only one instance will be opened.
             */
            window.location.assign(url);
            const tab = window.open(url);
            tab && tab.close();
        }
    }
    openOMG(options: IOpenOmgOptions): Promise<void> {
        return this.openLink({
            source: GNWP_SOURCES.OMG,
            view: 'iframe',
            priorityLevel: PRIORITY_LEVEL.OMG,
            options: {
                ExternalLinkInPopUp: '1',
                OpenedWithinGame: options.openedWithinGame,
                Messages: options.messages
            }
        })
    }
}





